import type { BenefitCategoryCode, DeductionCategoryCode } from './categories';

/**
 * Wire types for People > Benefits and Deductions, Phase 1. Every shape here follows docs/benefits-deductions-contract.md sections 3 and 4:
 * JSON camelCase, money as decimal numbers, dates `yyyy-MM-dd`, times ISO-8601 UTC, enums as STRING values (the new endpoints use string enums,
 * unlike the older numeric-enum endpoints). Where the contract leaves a shape open (catalog items, election list status values, the confirmed
 * bulk-assignment result) the choice made here is listed in docs/benefits-deductions-frontend-notes.md and flagged for the backend agents.
 */

// ---------------------------------------------------------------------------------------------------------------------------------------------
// Shared
// ---------------------------------------------------------------------------------------------------------------------------------------------

export interface Paged<T> {
  items: T[];
  page: number;
  pageSize: number;
  totalCount: number;
}

/** Plan and definition list filter. `incomplete` means configurationStatus is not Complete. */
export type StatusFilter = 'active' | 'inactive' | 'incomplete' | 'all';
export type PlanSort = 'name' | '-name' | 'category' | 'effectiveStart' | '-effectiveStart' | 'status';

export interface ListQuery {
  search?: string | undefined;
  category?: string | undefined;
  status?: StatusFilter | undefined;
  /** `yyyy-MM-dd`: plans whose effective window contains it; open ends count as containing. */
  effectiveOn?: string | undefined;
  sort?: PlanSort | undefined;
  page?: number | undefined;
  pageSize?: number | undefined;
}

export interface CatalogQuery {
  category?: string | undefined;
  search?: string | undefined;
  includeHidden?: boolean | undefined;
}

// ---------------------------------------------------------------------------------------------------------------------------------------------
// Enumerations (contract section 3)
// ---------------------------------------------------------------------------------------------------------------------------------------------

export const FUNDING_MODELS = ['EmployerPaid', 'EmployeePaid', 'Shared', 'NotApplicable'] as const;
export type FundingModel = (typeof FUNDING_MODELS)[number];
export const fundingModelLabels: Record<FundingModel, string> = {
  EmployerPaid: 'Employer paid',
  EmployeePaid: 'Employee paid',
  Shared: 'Shared between employer and employee',
  NotApplicable: 'Not applicable',
};

/** The seven methods the API accepts. `AgeBanded` and `CustomApprovedSchedule` are refused (400 UNSUPPORTED_CONTRIBUTION_METHOD) and never offered. */
export const CONTRIBUTION_METHODS = [
  'FixedPerPayroll', 'FixedPerMonth', 'PercentOfEligiblePay', 'EmployerPaidPercent', 'EmployeePaidPercent', 'TieredByCoverage', 'NoPayrollContribution',
] as const;
export type ContributionMethod = (typeof CONTRIBUTION_METHODS)[number];
export const contributionMethodLabels: Record<ContributionMethod, string> = {
  FixedPerPayroll: 'Fixed amount each payroll',
  FixedPerMonth: 'Fixed amount each month',
  PercentOfEligiblePay: 'Percent of eligible pay',
  EmployerPaidPercent: 'Employer pays a percent',
  EmployeePaidPercent: 'Employee pays a percent',
  TieredByCoverage: 'Amount depends on coverage level',
  NoPayrollContribution: 'No payroll contribution',
};
export const UNSUPPORTED_CONTRIBUTION_METHODS = ['AgeBanded', 'CustomApprovedSchedule'] as const;

export const COVERAGE_LEVELS = ['EmployeeOnly', 'EmployeeSpouse', 'EmployeeChild', 'EmployeeChildren', 'EmployeeFamily', 'Waived', 'Custom'] as const;
export type CoverageLevel = (typeof COVERAGE_LEVELS)[number];
export const coverageLevelLabels: Record<CoverageLevel, string> = {
  EmployeeOnly: 'Employee only',
  EmployeeSpouse: 'Employee and spouse',
  EmployeeChild: 'Employee and one child',
  EmployeeChildren: 'Employee and children',
  EmployeeFamily: 'Employee and family',
  Waived: 'Waived',
  Custom: 'Custom',
};

export const ENROLLMENT_METHODS = ['AdminEnrolled', 'EmployeeElected', 'AutoEnrolled', 'NotApplicable'] as const;
export type EnrollmentMethod = (typeof ENROLLMENT_METHODS)[number];
export const enrollmentMethodLabels: Record<EnrollmentMethod, string> = {
  AdminEnrolled: 'An administrator enrolls the employee',
  EmployeeElected: 'The employee elects it',
  AutoEnrolled: 'Employees are enrolled automatically',
  NotApplicable: 'Not applicable',
};

export const PAYROLL_FREQUENCIES = ['EveryPayroll', 'Monthly', 'Quarterly', 'Annually', 'OneTime'] as const;
export type PayrollFrequency = (typeof PAYROLL_FREQUENCIES)[number];
export const payrollFrequencyLabels: Record<PayrollFrequency, string> = {
  EveryPayroll: 'Every payroll',
  Monthly: 'Monthly',
  Quarterly: 'Quarterly',
  Annually: 'Annually',
  OneTime: 'One time',
};

export const CONFIGURATION_STATUSES = ['Complete', 'TaxReviewRequired', 'CalculationNotSupportedYet'] as const;
export type ConfigurationStatus = (typeof CONFIGURATION_STATUSES)[number];
export const configurationStatusLabels: Record<ConfigurationStatus, string> = {
  Complete: 'Complete',
  TaxReviewRequired: 'Configuration incomplete: tax review required',
  CalculationNotSupportedYet: 'Configuration incomplete: calculation not supported yet',
};

export const CREATES_PAYROLL_DEDUCTION = ['Never', 'Optional', 'Typical'] as const;
export type CreatesPayrollDeduction = (typeof CREATES_PAYROLL_DEDUCTION)[number];
export const createsPayrollDeductionLabels: Record<CreatesPayrollDeduction, string> = {
  Never: 'No payroll deduction',
  Optional: 'Payroll deduction is optional',
  Typical: 'Usually has a payroll deduction',
};

export const DEDUCTION_CLASSIFICATIONS = ['Voluntary', 'Involuntary', 'Statutory'] as const;
export type DeductionClassification = (typeof DEDUCTION_CLASSIFICATIONS)[number];
export const deductionClassificationLabels: Record<DeductionClassification, string> = {
  Voluntary: 'Voluntary',
  Involuntary: 'Involuntary (required by an order or law)',
  Statutory: 'Statutory (required by law)',
};

export const DEDUCTION_CALCULATION_TYPES = [
  'FixedAmount', 'PercentOfEligibleGross', 'PercentOfDisposableEarnings', 'ApprovedRetirementElectionPercent', 'ApprovedGarnishmentFormula',
  'RemainingBalanceUpToPerPayrollAmount',
] as const;
export type DeductionCalculationType = (typeof DEDUCTION_CALCULATION_TYPES)[number];
export const deductionCalculationTypeLabels: Record<DeductionCalculationType, string> = {
  FixedAmount: 'Fixed amount',
  PercentOfEligibleGross: 'Percent of eligible gross pay',
  PercentOfDisposableEarnings: 'Percent of disposable earnings',
  ApprovedRetirementElectionPercent: 'Retirement election percent',
  ApprovedGarnishmentFormula: 'Approved garnishment formula',
  RemainingBalanceUpToPerPayrollAmount: 'Remaining balance, up to an amount each payroll',
};
/** Phase 1 payroll honors only FixedAmount. Any other type can be saved but cannot be activated or assigned. */
export const SUPPORTED_CALCULATION_TYPE: DeductionCalculationType = 'FixedAmount';

export const ARREARS_BEHAVIORS = ['None', 'CarryForward', 'Stop'] as const;
export type ArrearsBehavior = (typeof ARREARS_BEHAVIORS)[number];
export const arrearsBehaviorLabels: Record<ArrearsBehavior, string> = {
  None: 'Do not track arrears',
  CarryForward: 'Carry the shortfall forward',
  Stop: 'Stop the deduction',
};

export const CATCH_UP_BEHAVIORS = ['None', 'AllowCatchUp'] as const;
export type CatchUpBehavior = (typeof CATCH_UP_BEHAVIORS)[number];
export const catchUpBehaviorLabels: Record<CatchUpBehavior, string> = { None: 'No catch-up', AllowCatchUp: 'Allow catch-up' };

export const STOP_RULES = ['None', 'StopAtAnnualLimit', 'StopAtGoalBalance', 'StopAtEndDate'] as const;
export type StopRule = (typeof STOP_RULES)[number];
export const stopRuleLabels: Record<StopRule, string> = {
  None: 'Never stop automatically',
  StopAtAnnualLimit: 'Stop at the annual limit',
  StopAtGoalBalance: 'Stop at the goal balance',
  StopAtEndDate: 'Stop at the end date',
};

export type TaxTreatmentCell = 'Taxable' | 'Exempt' | 'NotApplicable' | 'ReviewRequired';

// A company's own DECLARED tax profile for a custom benefit or deduction (contract 4.1.1.1 and 4.2.1): the treatment and one choice per tax, recorded as
// "needs review". It is stored and shown only: nothing in payroll or the tax engine reads it until a profile is approved.
export type DeclaredTaxTreatment = 'PreTax' | 'PostTax';
export type DeclaredTaxCell = 'Exempt' | 'Taxable';
export const DECLARED_TAX_CELL_KEYS = ['federalIncomeTax', 'socialSecurity', 'medicare', 'stateIncomeTax', 'localIncomeTax'] as const;
export type DeclaredTaxCellKey = (typeof DECLARED_TAX_CELL_KEYS)[number];
export const declaredTaxCellLabels: Record<DeclaredTaxCellKey, string> = {
  federalIncomeTax: 'Federal income tax',
  socialSecurity: 'Social Security',
  medicare: 'Medicare',
  stateIncomeTax: 'State income tax',
  localIncomeTax: 'Local income tax',
};

export interface DeclaredTaxProfileInput {
  treatment: DeclaredTaxTreatment;
  federalIncomeTax: DeclaredTaxCell | null;
  socialSecurity: DeclaredTaxCell | null;
  medicare: DeclaredTaxCell | null;
  stateIncomeTax: DeclaredTaxCell | null;
  localIncomeTax: DeclaredTaxCell | null;
}

export interface DeclaredTaxProfile extends DeclaredTaxProfileInput {
  status: 'NeedsReview' | 'Approved' | 'Rejected';
  declaredAtUtc: string | null;
}
export type ApprovalStatus = 'Draft' | 'UnderReview' | 'Approved' | 'Superseded' | 'Rejected';

// ---------------------------------------------------------------------------------------------------------------------------------------------
// Error codes (contract sections 4, 4.1 and 4.2): RFC 7807 `code`
// ---------------------------------------------------------------------------------------------------------------------------------------------

export const BdErrorCode = {
  DuplicateName: 'DUPLICATE_NAME',
  DuplicateCode: 'DUPLICATE_CODE',
  VersionConflict: 'VERSION_CONFLICT',
  ConfigurationIncomplete: 'CONFIGURATION_INCOMPLETE',
  PlanNotEffective: 'PLAN_NOT_EFFECTIVE',
  PlanInactive: 'PLAN_INACTIVE',
  CalculationNotSupportedYet: 'CALCULATION_NOT_SUPPORTED_YET',
  ProtectedCategory: 'PROTECTED_CATEGORY',
  TaxProfileNotApproved: 'TAX_PROFILE_NOT_APPROVED',
  UnsupportedContributionMethod: 'UNSUPPORTED_CONTRIBUTION_METHOD',
  IdempotencyKeyReused: 'IDEMPOTENCY_KEY_REUSED',
  TooManyEmployees: 'TOO_MANY_EMPLOYEES',
  /** A plan with elections cannot change its contribution method or category (the Compensation API's code for it). */
  PlanHasElections: 'PLAN_HAS_ELECTIONS',
  ExpectedVersionRequired: 'EXPECTED_VERSION_REQUIRED',
  InvalidQuery: 'INVALID_QUERY',
  CoverageLevelNotOffered: 'COVERAGE_LEVEL_NOT_OFFERED',
  ConfirmRequired: 'CONFIRM_REQUIRED',
  AssignmentNotAllowed: 'ASSIGNMENT_NOT_ALLOWED',
  AllEligibleUnavailable: 'ALL_ELIGIBLE_UNAVAILABLE',
  ValidationFailed: 'VALIDATION_FAILED',
  NotFound: 'NOT_FOUND',
  InvalidValue: 'INVALID_VALUE',
  DeclaredTaxProfileIncomplete: 'DECLARED_TAX_PROFILE_INCOMPLETE',
  DeclaredTaxProfileNotEditable: 'DECLARED_TAX_PROFILE_NOT_EDITABLE',
  DuplicateActiveAssignment: 'DUPLICATE_ACTIVE_ASSIGNMENT',
  DefinitionNotEffective: 'DEFINITION_NOT_EFFECTIVE',
  LimitExceedsDefinition: 'LIMIT_EXCEEDS_DEFINITION',
} as const;

// ---------------------------------------------------------------------------------------------------------------------------------------------
// Benefit catalog and plans (contract 4.1, 4.1.1)
// ---------------------------------------------------------------------------------------------------------------------------------------------

export interface BenefitCatalogItem {
  /** `ben.<category-code>.<kebab-name>`. Never changes after release. */
  code: string;
  name: string;
  categoryCode: BenefitCategoryCode;
  description: string;
  createsPayrollDeduction: CreatesPayrollDeduction;
  leaveLinked: boolean;
  /** "Balances and accruals are managed in Time and Leave." on a leave type, otherwise null. */
  leaveNote: string | null;
  version: number;
  sortOrder: number;
  /** Hidden for this company only. Hidden types are returned only with `includeHidden`. */
  isHidden: boolean;
}

export interface CoverageTier {
  coverageLevel: CoverageLevel;
  employeeAmount: number | null;
  employerAmount: number | null;
}

/** The writable part of a benefit plan (create body; update adds `expectedVersion`). */
export interface BenefitPlanInput {
  name: string;
  shortCode: string;
  categoryCode: BenefitCategoryCode;
  typeCode: string | null;
  description: string | null;
  providerName: string | null;
  planYear: number | null;
  effectiveStartDate: string | null;
  effectiveEndDate: string | null;
  eligibilityDescription: string | null;
  waitingPeriodDays: number | null;
  enrollmentMethod: EnrollmentMethod;
  fundingModel: FundingModel;
  contributionMethod: ContributionMethod;
  employeeContributionAmount: number | null;
  employerContributionAmount: number | null;
  employeeContributionPercent: number | null;
  employerContributionPercent: number | null;
  coverageTiers: CoverageTier[];
  payrollFrequency: PayrollFrequency;
  associatedDeductionCodeId: string | null;
  associatedEmployerContributionCodeId: string | null;
  taxProfileCode: string | null;
  w2ReportingCode: string | null;
  planDocumentReference: string | null;
  employeeVisibleDescription: string | null;
  internalNotes: string | null;
  isActive: boolean;
  /** Create only, for a custom plan: the Admin's declared tax profile (needs review). An update never sends one (the server refuses it). */
  declaredTaxProfile?: DeclaredTaxProfileInput | DeclaredTaxProfile | null | undefined;
}

export interface UpdateBenefitPlanInput extends BenefitPlanInput {
  expectedVersion: number;
}

/** `BenefitPlanResponse`: the richer view of a BenefitCode row. */
export interface BenefitPlan extends BenefitPlanInput {
  id: string;
  isCustom: boolean;
  configurationStatus: ConfigurationStatus;
  configurationIssues: string[];
  createsPayrollDeduction: boolean;
  leaveLinked: boolean;
  leaveNote: string | null;
  enrolledCount: number;
  concurrencyVersion: number;
  createdAtUtc: string;
  updatedAtUtc: string | null;
}

// ---------------------------------------------------------------------------------------------------------------------------------------------
// Benefit overview (contract 4.1.2)
// ---------------------------------------------------------------------------------------------------------------------------------------------

export type UpcomingKind = 'PlanStart' | 'PlanEnd' | 'ElectionStart';

export interface BenefitOverview {
  asOf: string;
  totalActivePlans: number;
  plansAwaitingConfiguration: number;
  plansApproachingExpiration: { id: string; name: string; effectiveEndDate: string }[];
  /** Distinct employees with an active, non-waived election. */
  enrolledEmployees: number;
  waivedEmployees: number;
  estimatedEmployeeContributionPerPayroll: number;
  estimatedEmployerContributionPerPayroll: number;
  contributionNote: string;
  upcoming: { kind: UpcomingKind; date: string; name: string }[];
  categoryBreakdown: { categoryCode: string; activePlans: number; enrolled: number }[];
  recentChanges: { eventType: string; occurredAtUtc: string; summary: string }[];
}

// ---------------------------------------------------------------------------------------------------------------------------------------------
// Benefit elections (existing endpoints, extended; company-wide list is new)
// ---------------------------------------------------------------------------------------------------------------------------------------------

/** The server's election list statuses: `active` is in effect today (a waiver in effect included), `upcoming` starts after today, `ended` has passed. */
export type ElectionStatusFilter = 'active' | 'upcoming' | 'ended' | 'all';

export interface BenefitElection {
  id: string;
  employeeId: string;
  /** The plan id (a plan is a BenefitCode row). */
  benefitCodeId: string;
  employeeContributionPercentOverride: number | null;
  companyContributionPercentOverride: number | null;
  amountOverride: number | null;
  effectiveStartDate: string;
  effectiveEndDate: string | null;
  coverageLevel: CoverageLevel | null;
  isWaived: boolean;
  qualifyingEventReason: string | null;
  enrollmentSource: EnrollmentMethod | null;
  /** Only the company-wide list carries these (the plan's name and category), so a table needs no second call. Null elsewhere. */
  planName: string | null;
  planCategoryCode: string | null;
  /** Bumped by every change to the election; an end can send it as `expectedVersion` so a stale screen cannot overwrite a newer change. */
  concurrencyVersion: number;
  createdAtUtc: string;
  updatedAtUtc: string | null;
}

export interface CreateBenefitElectionInput {
  benefitCodeId: string;
  employeeContributionPercentOverride?: number | null;
  companyContributionPercentOverride?: number | null;
  amountOverride?: number | null;
  effectiveStartDate: string;
  coverageLevel?: CoverageLevel | null;
  isWaived?: boolean;
  qualifyingEventReason?: string | null;
  enrollmentSource?: EnrollmentMethod | null;
  /** Optional: when the election stops applying (after the start date). */
  effectiveEndDate?: string | null;
  /** True when this change replaces the employee's current election of the plan: the server refuses a second open one otherwise (409 DUPLICATE_ACTIVE_ASSIGNMENT). */
  replaceCurrent?: boolean;
}

export interface ElectionQuery {
  planId?: string | undefined;
  employeeId?: string | undefined;
  status?: ElectionStatusFilter | undefined;
  page?: number | undefined;
  pageSize?: number | undefined;
}

// ---------------------------------------------------------------------------------------------------------------------------------------------
// Deductions: catalog, definitions, summary, tax profiles, limit rules (contract 4.2)
// ---------------------------------------------------------------------------------------------------------------------------------------------

export interface DeductionCatalogItem {
  /** `ded.<category-code>.<kebab-name>`. */
  code: string;
  name: string;
  categoryCode: DeductionCategoryCode;
  categoryName: string;
  description: string;
  classification: DeductionClassification;
  /** Statutory rows carry none: the system calculates them. */
  defaultCalculationType: DeductionCalculationType | null;
  isStatutory: boolean;
  /** True for a statutory row: system-calculated, never hideable, no route changes it. */
  isReadOnly: boolean;
  /** True for garnishments and levies. */
  requiresRuleReview: boolean;
  version: number;
  sortOrder: number;
  isHidden: boolean;
}

export interface DeductionDefinitionInput {
  name: string;
  shortCode: string;
  categoryCode: DeductionCategoryCode;
  typeCode: string | null;
  description: string | null;
  classification: DeductionClassification;
  calculationType: DeductionCalculationType;
  frequency: PayrollFrequency;
  /** The fixed dollar amount, or the percent for a percent calculation type. */
  defaultAmount: number | null;
  defaultCompanyContribution: number | null;
  effectiveStartDate: string | null;
  effectiveEndDate: string | null;
  annualLimit: number | null;
  perPayPeriodLimit: number | null;
  goalBalance: number | null;
  /** 1 to 99, lower first. Stored only in Phase 1: nothing uses it yet. */
  priority: number | null;
  arrearsBehavior: ArrearsBehavior;
  catchUpBehavior: CatchUpBehavior;
  stopRule: StopRule;
  associatedBenefitCodeId: string | null;
  requiresEmployeeAuthorization: boolean;
  remittanceRecipientName: string | null;
  employeeVisibleLabel: string | null;
  internalNotes: string | null;
  taxProfileCode: string | null;
  limitRuleCode: string | null;
  isActive: boolean;
  /** Create only, for a custom definition: the Admin's declared tax profile (needs review). An update never sends one (the server refuses it). */
  declaredTaxProfile?: DeclaredTaxProfileInput | null | undefined;
}

export interface UpdateDeductionDefinitionInput extends DeductionDefinitionInput {
  expectedVersion: number;
}

/** `DeductionCodeResponse` extended (contract 4.2). A legacy row has null for every new field and a null configurationStatus (treated as Complete). */
export interface DeductionDefinition {
  id: string;
  name: string;
  shortCode: string | null;
  categoryCode: DeductionCategoryCode | null;
  typeCode: string | null;
  description: string | null;
  classification: DeductionClassification | null;
  calculationType: DeductionCalculationType | null;
  frequency: PayrollFrequency | null;
  taxType: number;
  amountType: number;
  defaultAmount: number | null;
  defaultCompanyContribution: number | null;
  w2Code: string | null;
  exemptFederal: boolean | null;
  exemptFica: boolean | null;
  exemptState: boolean | null;
  glAccountDefault: string | null;
  effectiveStartDate: string | null;
  effectiveEndDate: string | null;
  annualLimit: number | null;
  perPayPeriodLimit: number | null;
  goalBalance: number | null;
  priority: number | null;
  arrearsBehavior: ArrearsBehavior | null;
  catchUpBehavior: CatchUpBehavior | null;
  stopRule: StopRule | null;
  associatedBenefitCodeId: string | null;
  requiresEmployeeAuthorization: boolean | null;
  remittanceRecipientName: string | null;
  employeeVisibleLabel: string | null;
  internalNotes: string | null;
  taxProfileCode: string | null;
  limitRuleCode: string | null;
  isActive: boolean;
  isCustom: boolean;
  /** The company's declared tax profile (needs review), or null when none was declared. */
  declaredTaxProfile?: DeclaredTaxProfile | null | undefined;
  configurationStatus: ConfigurationStatus | null;
  configurationIssues: string[];
  concurrencyVersion: number;
  isSystemDefinition: boolean;
  createdAtUtc: string;
  updatedAtUtc: string | null;
}

export interface DeductionSummary {
  totalActive: number;
  awaitingConfiguration: number;
  endingSoon: { id: string; name: string; effectiveEndDate: string }[];
  byCategory: { categoryCode: string; active: number; total: number }[];
  recentChanges: { eventType: string; occurredAtUtc: string; summary: string }[];
}

export interface TaxProfile {
  code: string;
  name: string;
  description: string | null;
  version: number;
  effectiveFrom: string | null;
  effectiveTo: string | null;
  status: ApprovalStatus;
  approvedForPayroll: boolean;
  w2Box: string | null;
  w2Code: string | null;
  sourceTitle: string | null;
  sourceUrl: string | null;
  isSynthetic: boolean;
  treatments: Record<string, TaxTreatmentCell>;
}

export interface TaxProfileQuery {
  asOf?: string | undefined;
  includeUnapproved?: boolean | undefined;
}

export interface LimitRule {
  code: string;
  name: string;
  version: number;
  effectiveFrom: string | null;
  effectiveTo: string | null;
  /** Seed rows carry no amount: no number is invented or hard-coded. */
  amount: number | null;
  unit: string;
  ageThreshold: number | null;
  status: ApprovalStatus;
  isSynthetic: boolean;
}

// ---------------------------------------------------------------------------------------------------------------------------------------------
// Company-wide deduction elections (existing `GET /api/deduction-elections`) and bulk assignment (contract 4.1.3)
// ---------------------------------------------------------------------------------------------------------------------------------------------

export interface DeductionElection {
  id: string;
  employeeId: string;
  deductionCodeId: string;
  amountOverride: number | null;
  companyContributionOverride: number | null;
  title: string | null;
  effectiveStartDate: string;
  effectiveEndDate: string | null;
  createdAtUtc: string;
  updatedAtUtc: string | null;
}

export type BulkScope = 'Employees' | 'AllEligible';
export type ExcludedReason = 'AlreadyAssigned' | 'NotInCompany' | 'NotActive' | 'HiredAfterEffectiveDate' | 'Duplicate';

export interface BulkAssignmentRequest {
  deductionCodeId: string;
  scope: BulkScope;
  employeeIds?: string[] | undefined;
  effectiveStartDate: string;
  amountOverride?: number | null | undefined;
  companyContributionOverride?: number | null | undefined;
}

export interface BulkAssignmentConfirm extends BulkAssignmentRequest {
  idempotencyKey: string;
  confirm: true;
}

export interface BulkAssignmentPreview {
  deduction: { id: string; name: string; calculationType: DeductionCalculationType | null; configurationStatus: ConfigurationStatus | null; taxProfileCode: string | null; isActive: boolean };
  effectiveStartDate: string;
  affectedCount: number;
  excluded: { employeeId: string; reason: ExcludedReason }[];
  /** `amount` is the TOTAL each payroll across the affected employees; `amountPerEmployee` is one employee's share. Both are null when they cannot be stated. */
  estimatePerPayroll: { amount: number | null; amountPerEmployee: number | null; basis: string };
  limits: { annualLimit: number | null; perPayPeriodLimit: number | null; goalBalance: number | null };
  conflicts: string[];
  errors: { code: string; message: string }[];
  canConfirm: boolean;
}

/** The confirmed result. The contract says a replay "returns the original result with replayed: true"; the exact body is not spelled out, so the
 * Http mapping accepts `createdCount` or `affectedCount`. */
export interface BulkAssignmentResult {
  deductionCodeId: string;
  effectiveStartDate: string;
  createdCount: number;
  excluded: { employeeId: string; reason: ExcludedReason }[];
  replayed: boolean;
}

/** An existing per-employee garnishment record (Compensation `GET /api/employees/{id}/garnishments`). Read only here: Phase 1 adds no limit or priority logic. */
export interface GarnishmentRecord {
  id: string;
  employeeId: string;
  caseNumber: string;
  typeLabel: string;
  priority: number;
  amountTypeLabel: string;
  amount: number;
  maxPercentOfDisposableEarnings: number | null;
  issuingAuthority: string | null;
  effectiveStartDate: string;
  effectiveEndDate: string | null;
}

// ---------------------------------------------------------------------------------------------------------------------------------------------
// The company catalog: one read model over benefit plans (Compensation) and deduction definitions (PayrollConfig). GET /api/benefit-deduction-catalog
// (contract "Unified catalog read"). The Employee Profile Setup modal reads only this; no list of benefits or deductions is written into a component.
// ---------------------------------------------------------------------------------------------------------------------------------------------

export type CatalogItemType = 'Benefit' | 'Deduction';
export type CatalogTypeFilter = CatalogItemType | 'All';
export type CatalogSourceState = 'Included' | 'NoPermission' | 'Unavailable';

export interface CompanyCatalogItem {
  /** `benefit:{id}` or `deduction:{id}`: stable and unique across both kinds. */
  key: string;
  itemType: CatalogItemType;
  /** The id the matching assignment endpoint takes (a benefit plan id or a deduction code id). */
  id: string;
  name: string;
  shortCode: string | null;
  categoryCode: string;
  categoryName: string;
  isCustom: boolean;
  isActive: boolean;
  effectiveStartDate: string | null;
  effectiveEndDate: string | null;
  effectiveToday: boolean;
  /** The definition's own calculation method (a contribution method for a plan, a calculation type for a deduction). Read-only here. */
  calculationMethod: string | null;
  frequency: string | null;
  defaultEmployeeAmount: number | null;
  defaultEmployeePercent: number | null;
  defaultEmployerAmount: number | null;
  defaultEmployerPercent: number | null;
  /** The coverage levels a tiered plan prices; empty otherwise. */
  coverageLevels: CoverageLevel[];
  taxProfileCode: string | null;
  /** The deduction definition's own treatment. Null for a benefit plan (its treatment comes from an approved tax profile): the screen never claims one. */
  taxTreatment: 'PreTax' | 'PostTax' | null;
  configurationStatus: string;
  /** Advisory: the server re-checks every rule when an assignment is written. */
  assignable: boolean;
  notAssignableCode: string | null;
  notAssignableReason: string | null;
  concurrencyVersion: number;
}

export interface CompanyCatalogSource {
  itemType: CatalogItemType;
  status: CatalogSourceState;
}

export interface CompanyCatalogPage extends Paged<CompanyCatalogItem> {
  /** Which half could not be read (no permission, or the owner was unavailable). A missing half is reported, never shown as empty. */
  sources: CompanyCatalogSource[];
}

export interface CompanyCatalogQuery {
  type?: CatalogTypeFilter | undefined;
  search?: string | undefined;
  category?: string | undefined;
  /** `active` by default. */
  status?: StatusFilter | undefined;
  page?: number | undefined;
  pageSize?: number | undefined;
}
